import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import * as api from '../services/api';

type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error';

interface DataContextType {
  syncStatus: SyncStatus;
  lastSync: string | null;
  isOnline: boolean;
  fetchAll: <T>(entity: string) => Promise<T[]>;
  fetchById: <T>(entity: string, id: string) => Promise<T | null>;
  save: <T extends { id?: string | number }>(entity: string, data: T) => Promise<T | null>;
  saveBatch: <T extends { id?: string | number }>(entity: string, items: T[]) => Promise<number>;
  remove: (entity: string, id: string) => Promise<boolean>;
  refreshSync: () => Promise<void>;
}

export const DataContext = createContext<DataContextType | null>(null);

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData deve ser usado dentro de DataProvider');
  return ctx;
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);

  const refreshSync = useCallback(async () => {
    setSyncStatus('syncing');
    const healthy = await api.healthCheck();
    setIsOnline(healthy);
    setSyncStatus(healthy ? 'synced' : 'error');
    if (healthy) setLastSync(new Date().toISOString());
  }, []);

  useEffect(() => {
    refreshSync();
  }, [refreshSync]);

  const fetchAll = useCallback(async <T,>(entity: string): Promise<T[]> => {
    if (!isOnline) return [];
    const data = await api.listEntities<T>(entity as api.Entity);
    return data || [];
  }, [isOnline]);

  const fetchById = useCallback(async <T,>(entity: string, id: string): Promise<T | null> => {
    if (!isOnline) return null;
    return api.getEntity<T>(entity as api.Entity, id);
  }, [isOnline]);

  const save = useCallback(async <T extends { id?: string | number }>(entity: string, data: T): Promise<T | null> => {
    if (!isOnline) return null;
    const saved = await api.saveEntity(entity as api.Entity, data);
    if (saved) setLastSync(new Date().toISOString());
    return saved;
  }, [isOnline]);

  const saveBatch = useCallback(async <T extends { id?: string | number }>(entity: string, items: T[]): Promise<number> => {
    if (!isOnline) return 0;
    const count = await api.saveBatch(entity as api.Entity, items);
    if (count > 0) setLastSync(new Date().toISOString());
    return count;
  }, [isOnline]);

  const remove = useCallback(async (entity: string, id: string): Promise<boolean> => {
    if (!isOnline) return false;
    const res = await api.deleteEntityResult(entity as api.Entity, id);
    if (!res.ok) throw new Error(res.error || `Falha ao remover ${entity}.`);
    setLastSync(new Date().toISOString());
    return true;
  }, [isOnline]);

  return (
    <DataContext.Provider
      value={{
        syncStatus,
        lastSync,
        isOnline,
        fetchAll,
        fetchById,
        save,
        saveBatch,
        remove,
        refreshSync,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

